'use client'

import LiveryCard from '@/components/_cards/livery/livery-card'
import Error from '@/components/common/error'
import CircleWithUpArrow from '@/components/icons/circle-with-up-arrow'
import SearchHeader from '@/components/search/search-header'
import { Skeleton } from '@/components/ui/skeleton'
import Image from 'next/image'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useSWR from 'swr'
import { LiveryFooter } from '../../components/livery-home-page'

export default function LiverySearchPage({
  searchParams,
  session,
  initialResults = null,
  games = [],
  platforms = [],
}) {
  // State management
  const [pageNumber, setPageNumber] = useState(1)
  const [totalCount, setTotalCount] = useState(initialResults?.total || 0)
  const [liveryDesigns, setLiveryDesigns] = useState(
    initialResults?.liveryDesigns || [],
  )
  const [hasMore, setHasMore] = useState(
    (initialResults?.liveryDesigns?.length || 0) === 12 &&
      (initialResults?.liveryDesigns?.length || 0) <
        (initialResults?.total || 0),
  )
  const [initialLoad, setInitialLoad] = useState(!initialResults)
  const [isChangingFilters, setIsChangingFilters] = useState(false)

  const searchLimit = 12

  // Create observer ref for infinite scroll
  const loadingRef = useRef(null)
  const processedResponse = useRef(null)

  // Build normalized filters from search params
  const normalizedFilters = useMemo(
    () => ({
      platform:
        searchParams.platform && searchParams.platform !== 'Any'
          ? searchParams.platform
          : null,
      game:
        searchParams.game && searchParams.game !== 'Any'
          ? searchParams.game
          : null,
      carClasses:
        searchParams.carClasses && searchParams.carClasses !== 'Any'
          ? searchParams.carClasses
          : null,
      keyword: searchParams.keyword || null,
      orderBy: searchParams.orderBy || 'most-popular',
    }),
    [
      searchParams.platform,
      searchParams.game,
      searchParams.carClasses,
      searchParams.keyword,
      searchParams.orderBy,
    ],
  )

  // Effect to reset pagination when search params change
  useEffect(() => {
    setPageNumber(1)
    setInitialLoad(true)
    setIsChangingFilters(true)
  }, [
    normalizedFilters.platform,
    normalizedFilters.game,
    normalizedFilters.carClasses,
    normalizedFilters.keyword,
    normalizedFilters.orderBy,
  ])

  useEffect(() => {
    if (!initialResults) {
      return
    }

    const receivedDesigns = initialResults.liveryDesigns || []
    const total = initialResults.total || 0

    setTotalCount(total)
    setLiveryDesigns(receivedDesigns)
    setInitialLoad(false)
    setIsChangingFilters(false)
    setHasMore(
      receivedDesigns.length === searchLimit && receivedDesigns.length < total,
    )
  }, [initialResults, searchLimit])

  const swrKey = useMemo(() => {
    const params = new URLSearchParams({
      page: String(pageNumber),
      pageSize: String(searchLimit),
      orderBy: normalizedFilters.orderBy,
    })

    if (normalizedFilters.platform)
      params.set('platform', normalizedFilters.platform)
    if (normalizedFilters.game) params.set('game', normalizedFilters.game)
    if (normalizedFilters.carClasses) {
      params.set('carClasses', normalizedFilters.carClasses)
    }
    if (normalizedFilters.keyword)
      params.set('keyword', normalizedFilters.keyword)

    return `/api/internal/search/liveries?${params.toString()}`
  }, [normalizedFilters, pageNumber, searchLimit])

  const fetcher = useCallback(async (url) => {
    const response = await fetch(url)

    if (!response.ok) {
      throw new Error('Failed to load liveries')
    }

    return response.json()
  }, [])
  // Data fetching with SWR
  const {
    data,
    error,
    isLoading: loading,
    mutate,
  } = useSWR(swrKey, fetcher, {
    revalidateOnFocus: false,
    dedupingInterval: 600000,
    revalidateOnMount: pageNumber !== 1 || !initialResults,
    keepPreviousData: false,
    fallbackData: pageNumber === 1 ? initialResults || undefined : undefined,
  })

  // Process fetched data
  useEffect(() => {
    if (!data || processedResponse.current === data) return
    processedResponse.current = data

    // Extract and log received data for debugging
    const receivedDesigns = data.liveryDesigns || []
    const total = data.total || 0

    setTotalCount(total)

    if (initialLoad || isChangingFilters) {
      setLiveryDesigns(receivedDesigns)
      setInitialLoad(false)
      setIsChangingFilters(false)

      // Set hasMore based on initial data
      const shouldHaveMore =
        receivedDesigns.length === searchLimit && receivedDesigns.length < total
      setHasMore(shouldHaveMore)
    } else if (pageNumber > 1) {
      // We're loading more data for additional pages

      // Create a Set of existing IDs to avoid duplicates
      const existingIds = new Set(liveryDesigns.map((design) => design.id))

      // Only add new designs that don't already exist in the current set
      const newDesigns = receivedDesigns.filter(
        (design) => !existingIds.has(design.id),
      )

      if (newDesigns.length > 0) {
        setLiveryDesigns((prev) => {
          const updated = [...prev, ...newDesigns]
          return updated
        })

        // Check if we should continue to have more based on what we received
        const shouldHaveMore =
          newDesigns.length === searchLimit &&
          liveryDesigns.length + newDesigns.length < total
        setHasMore(shouldHaveMore)
      } else {
        // No new designs were added - we've reached the end or have duplicates
        setHasMore(false)
      }
    }
  }, [
    data,
    initialLoad,
    isChangingFilters,
    searchLimit,
    pageNumber,
    liveryDesigns,
  ])

  // Create a stable callback function for the intersection observer
  const loadMoreItems = useCallback(() => {
    if (!hasMore || loading || isChangingFilters || error) {
      return
    }

    setPageNumber((prevPage) => prevPage + 1)
  }, [hasMore, loading, isChangingFilters, error])

  // Set up intersection observer for infinite scrolling
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMoreItems()
        }
      },
      {
        threshold: 0.01, // Extremely low threshold to detect as soon as even 1% is visible
        rootMargin: '500px', // Large margin to pre-load before fully visible
      },
    )

    const currentRef = loadingRef.current
    if (currentRef) {
      observer.observe(currentRef)
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef)
      }
    }
  }, [loadMoreItems])

  // Register a scroll event listener as backup with throttling
  useEffect(() => {
    let timeout = null
    let lastScrollY = window.scrollY

    const handleScroll = () => {
      // Skip if we recently handled a scroll or if already loading
      if (timeout || loading || !hasMore || isChangingFilters) return

      const currentScrollY = window.scrollY
      const isScrollingDown = currentScrollY > lastScrollY
      lastScrollY = currentScrollY

      // Only check when scrolling down
      if (!isScrollingDown) return

      // Throttle the scroll events
      timeout = setTimeout(() => {
        timeout = null

        if (!loadingRef.current) return

        const rect = loadingRef.current.getBoundingClientRect()
        // Check if we're near the bottom (500px buffer)
        const isNearBottom = rect.top <= window.innerHeight + 500

        if (isNearBottom && hasMore && !loading && !isChangingFilters) {
          loadMoreItems()
        }
      }, 200)
    }

    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
      if (timeout) clearTimeout(timeout)
    }
  }, [loadMoreItems, hasMore, loading, isChangingFilters])

  // Calculate if we should show loading state
  const showLoadingState = loading && (initialLoad || isChangingFilters)

  // Scroll to top function
  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  // Memoize search summary text
  const searchSummary = useMemo(
    () =>
      [
        searchParams.platform || 'All Platforms',
        searchParams.game || 'All Games',
        searchParams.carClasses || 'All Cars',
      ].join(' • '),
    [searchParams.platform, searchParams.game, searchParams.carClasses],
  )

  return (
    <>
      {/* Hero search section */}
      <div className="max-w-(--breakpoint-desktop-lg) relative mx-auto mb-10 w-full rounded-b-[20px] bg-neutral-blue-800 pb-8 pt-[29px] desktop-xs:rounded-b-[40px] desktop-xs:pb-[48px] desktop-sm:rounded-b-[60px] desktop-sm:py-[75px]">
        <div className="absolute inset-y-0 right-0 hidden h-full w-1/2 overflow-hidden rounded-br-[60px] desktop-sm:block">
          <Image
            src="/img/frontend/homeSearchImg.webp"
            alt="Home Image"
            height={1000}
            width={2560}
            className="relative left-[2px] h-full object-cover"
            priority
          />

          <div className="bg-linear-to-r absolute right-0 top-0 h-full w-full from-neutral-blue-800 via-[#08111B] via-20% to-[#08111bad]" />
          <div className="bg-linear-to-t absolute right-0 top-0 h-full w-full from-neutral-blue-800 to-[#08111b51] to-20%" />
        </div>

        <div className="screen-grid relative gap-5">
          <div className="grid-indent flex flex-col gap-y-8">
            <h2 className="font-inter text-h-3 font-bold desktop-xs:text-h-1 desktop-sm:w-[60%]">
              <span className="gf-gradient-text">Search </span>
              sim racing liveries
            </h2>

            <SearchHeader
              searchParams={searchParams}
              games={games}
              platforms={platforms}
              textPlaceholder="e.g. 1960s Gulf"
              linkPrefix="sim-racing-liveries/search"
              hideDays={true}
              hideCarClass={false}
            />
          </div>
        </div>
      </div>

      {/* Search results section */}
      <div className="screen-grid">
        {/* Header with result count and filters */}
        <div className="grid-indent">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-col gap-1">
              <span className="text-p-2 font-normal capitalize text-neutral-blue-500">
                {totalCount} results · {searchSummary}
              </span>
            </div>
          </div>
        </div>

        {/* Results grid */}
        <div className="grid-indent">
          {error && (
            <button
              type="button"
              onClick={() => mutate()}
              className="min-h-11 rounded border px-4"
            >
              Retry loading results
            </button>
          )}
          {error && (
            <Error
              icon="info"
              size="alert"
              className="mx-auto my-16 w-full max-w-7xl bg-red-600 text-white"
              error={error}
            />
          )}
          {showLoadingState && liveryDesigns.length > 0 && (
            <p role="status">Updating results…</p>
          )}
          {showLoadingState && !liveryDesigns.length ? (
            <div className="grid w-full grid-cols-1 gap-8 py-10 md:grid-cols-2 desktop-xs:grid-cols-3">
              {Array(12)
                .fill(null)
                .map((_, index) => (
                  <div key={index} className="flex flex-col gap-2">
                    <Skeleton className="h-[225px] w-full" />
                    <Skeleton className="h-[20px] w-full" />
                    <div className="flex gap-1">
                      <Skeleton className="h-[15px] w-[30px]" />
                      <Skeleton className="h-[15px] w-[30px]" />
                      <Skeleton className="h-[15px] w-[30px]" />
                    </div>
                    <Skeleton className="h-[20px] w-[70px]" />
                  </div>
                ))}
            </div>
          ) : !liveryDesigns.length ? (
            Object.values(searchParams).filter((v) => v !== '').length > 0 && (
              <div className="grid grid-cols-1">
                <p className="w-full pb-[70px] pt-10 text-center text-neutral-blue-500">
                  Sorry, there are currently no designs that match the search
                  criteria...
                </p>
              </div>
            )
          ) : (
            <div className="grid w-full grid-cols-1 gap-8 py-10 md:grid-cols-2 desktop-xs:grid-cols-3">
              {liveryDesigns.map((design) => (
                <LiveryCard key={design.id} livery={design} session={session} />
              ))}
            </div>
          )}

          {/* Loading indicator for infinite scroll - always in DOM but conditionally visible */}
          <div
            ref={loadingRef}
            id="loading-indicator"
            className={`col-span-full my-8 flex justify-center py-10 ${
              hasMore ? 'visible' : 'invisible h-4'
            }`}
          >
            <div className="flex flex-col items-center space-y-3">
              <p className="text-neutral-blue-500">
                {loading && !initialLoad && !isChangingFilters
                  ? 'Loading more results...'
                  : hasMore
                    ? 'Scroll for more results'
                    : 'End of results'}
              </p>
              <div className="flex space-x-2">
                <div
                  className={`h-3 w-3 rounded-full bg-neutral-blue-500 ${loading && !initialLoad ? 'animate-bounce' : ''}`}
                ></div>
                <div
                  className={`h-3 w-3 rounded-full bg-neutral-blue-500 ${loading && !initialLoad ? 'animate-bounce' : ''}`}
                  style={{ animationDelay: '0.2s' }}
                ></div>
                <div
                  className={`h-3 w-3 rounded-full bg-neutral-blue-500 ${loading && !initialLoad ? 'animate-bounce' : ''}`}
                  style={{ animationDelay: '0.4s' }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        {/* Back to top button */}
        <div
          className="col-span-full mx-auto cursor-pointer font-inter text-p-1 font-normal text-neutral-blue-500"
          onClick={scrollToTop}
        >
          <div className="group flex items-center align-middle">
            <div className="transition-all ease-in-out group-hover:text-white">
              Back to top
            </div>
            <CircleWithUpArrow
              className="ml-1 w-5 stroke-neutral-blue-500 stroke-2 transition-all ease-in-out group-hover:stroke-white"
              stroke="neutral-blue-500"
            />
          </div>
        </div>
      </div>

      <div className="screen-grid *:grid-indent">
        <div className="divider my-20" />
      </div>

      <div className="mt-[100px]">
        <LiveryFooter session={session} />
      </div>
    </>
  )
}
